import {
  Editor,
  Text,
  Node,
  Node as SlateNode,
  Transforms,
  type BaseRange
} from 'slate';

export const isRangeValid = (editor: Editor, range: BaseRange | null): boolean => {
  if (!range?.anchor?.path || !range?.focus?.path) return false;

  return (
    Editor.hasPath(editor, range.anchor.path) &&
    Editor.hasPath(editor, range.focus.path)
  );
};

/** Clears selection when paths no longer exist in the document tree. */
export const deselectIfInvalid = (editor: Editor): boolean => {
  if (!editor.selection) return false;
  if (isRangeValid(editor, editor.selection)) return false;

  editor.selection = null;
  try {
    Transforms.deselect(editor);
  } catch {
    // ignore
  }

  return true;
};

export const getSafeCollapsedSelection = (editor: Editor): BaseRange | null => {
  if (editor.selection && isRangeValid(editor, editor.selection)) {
    return editor.selection;
  }

  try {
    const end = Editor.end(editor, []);

    return {anchor: end, focus: end};
  } catch {
    return null;
  }
};

/** Move caret to the end of the document (safe for post-sidebar-insert). */
export const selectAtDocumentEnd = (editor: Editor): boolean => {
  try {
    const end = Editor.end(editor, []);
    if (!Editor.hasPath(editor, end.path)) return false;

    Transforms.select(editor, {anchor: end, focus: end});

    return true;
  } catch {
    return false;
  }
};

/**
 * Select document end now and again after reflow (bulk insert paginates ~300ms).
 */
/**
 * Slate plugin: drop invalid selections before/after every operation and onChange.
 * Prevents slate-react from crashing when the document tree changes under the caret.
 */
export const withSelectionGuard = <T extends Editor>(editor: T): T => {
  const {apply, onChange} = editor;

  editor.apply = (op) => {
    deselectIfInvalid(editor);
    apply(op);
    deselectIfInvalid(editor);
  };

  editor.onChange = () => {
    deselectIfInvalid(editor);
    onChange();
  };

  return editor;
};

export const scheduleSelectAtDocumentEnd = (
  editor: Editor,
  options?: {afterReflowMs?: number}
): void => {
  selectAtDocumentEnd(editor);

  if (typeof window === 'undefined') return;

  window.requestAnimationFrame(() => {
    selectAtDocumentEnd(editor);
  });

  const delay = options?.afterReflowMs ?? 0;
  if (delay > 0) {
    window.setTimeout(() => {
      selectAtDocumentEnd(editor);
    }, delay);
  }
};

export const sanitizeRange = (range: any, editor: any): any => {
  if (!range) return null;

  const toTextPoint = (point: any, isAnchor: boolean) => {
    if (!point || !Array.isArray(point.path)) return null;

    const findDeepestText = (startPath: any[]) => {
      const currentPath = [...startPath];
      try {
        let node = SlateNode.get(editor, currentPath);
        while (
          !Text.isText(node) &&
          (node as any).children &&
          (node as any).children.length > 0
        ) {
          const childIndex = isAnchor ? 0 : (node as any).children.length - 1;
          currentPath.push(childIndex);
          node = (node as any).children[childIndex];
        }
        if (Text.isText(node)) {
          const text = node.text || '';
          const offset = isAnchor ? 0 : text.length;

          return {
            path: currentPath,
            offset:
              startPath.join(',') === currentPath.join(',')
                ? Math.max(0, Math.min(Number(point.offset) || 0, text.length))
                : offset
          };
        }
      } catch (error) {
        // Node retrieval failed
      }

      return null;
    };

    const directResult = findDeepestText(point.path);
    if (directResult) return directResult;

    for (let i = point.path.length - 1; i >= 0; i--) {
      const candidatePath = point.path.slice(0, i);
      if (candidatePath.length === 0) break;
      const ancestorResult = findDeepestText(candidatePath);
      if (ancestorResult) return ancestorResult;
    }

    try {
      return isAnchor ? Editor.start(editor, []) : Editor.end(editor, []);
    } catch (error) {
      return null;
    }
  };

  const anchor = toTextPoint(range.anchor, true);
  const focus = toTextPoint(range.focus, false);
  if (!anchor || !focus) return null;

  return {anchor, focus};
};

/** Restore caret/selection inside header or footer after pagination reflow clones nodes. */
export interface HeaderFooterSelectionMeta {
  pageIndex: number;
  wrapperType: 'header-wrapper' | 'footer-wrapper';
  anchorBlockIndex: number;
  focusBlockIndex: number;
  anchorOffset: number;
  focusOffset: number;
}

export const captureHeaderFooterSelectionMeta = (
  editor: Editor,
  selection: BaseRange | null
): HeaderFooterSelectionMeta | null => {
  if (!selection?.anchor?.path?.length || !selection.focus?.path?.length) {
    return null;
  }

  try {
    const wrapperNode = Node.get(editor, selection.anchor.path.slice(0, 2)) as {
      type?: string;
    };
    if (
      wrapperNode.type !== 'header-wrapper' &&
      wrapperNode.type !== 'footer-wrapper'
    ) {
      return null;
    }

    const anchorBlockIndex = selection.anchor.path[2];
    const focusBlockIndex = selection.focus.path[2];
    if (anchorBlockIndex === undefined || focusBlockIndex === undefined) {
      return null;
    }

    return {
      pageIndex: selection.anchor.path[0],
      wrapperType: wrapperNode.type as 'header-wrapper' | 'footer-wrapper',
      anchorBlockIndex,
      focusBlockIndex,
      anchorOffset: selection.anchor.offset,
      focusOffset: selection.focus.offset
    };
  } catch {
    return null;
  }
};

export const restoreHeaderFooterSelection = (
  editor: Editor,
  meta: HeaderFooterSelectionMeta
): boolean => {
  try {
    const page = editor.children[meta.pageIndex] as {
      children?: Array<{type?: string}>;
    };
    const children = page?.children;
    if (!children?.length) return false;

    const wrapperIndex =
      meta.wrapperType === 'header-wrapper'
        ? children.findIndex((c) => c?.type === 'header-wrapper')
        : (() => {
            for (let i = children.length - 1; i >= 0; i--) {
              if (children[i]?.type === 'footer-wrapper') return i;
            }

            return -1;
          })();

    if (wrapperIndex < 0) return false;

    const newAnchorPath = [
      meta.pageIndex,
      wrapperIndex,
      meta.anchorBlockIndex,
      0
    ];
    const newFocusPath = [meta.pageIndex, wrapperIndex, meta.focusBlockIndex, 0];
    if (
      !Editor.hasPath(editor, newAnchorPath) ||
      !Editor.hasPath(editor, newFocusPath)
    ) {
      return false;
    }

    const anchorText = Node.string(Node.get(editor, newAnchorPath.slice(0, 3)));
    const focusText = Node.string(Node.get(editor, newFocusPath.slice(0, 3)));

    Transforms.select(editor, {
      anchor: {
        path: newAnchorPath,
        offset: Math.min(meta.anchorOffset, anchorText.length)
      },
      focus: {
        path: newFocusPath,
        offset: Math.min(meta.focusOffset, focusText.length)
      }
    });

    return true;
  } catch {
    return false;
  }
};
